import { StageTag, StatusPill } from "@/components/StatusPill";
import { UnverifiedBadge } from "@/components/UnverifiedBadge";
import { ProjectReportButton } from "@/components/ProjectReportButton";
import { fmtCr, fmtDay, TERM_COLORS } from "@/lib/api";

/** Every column the register can show. `off` means it is hidden until an officer asks for it. */
export const PROJECT_COLUMNS = [
  { key: "code", label: "Code" },
  { key: "title", label: "Project" },
  { key: "plant", label: "Unit" },
  { key: "status", label: "Status" },
  { key: "term", label: "Term" },
  { key: "phase", label: "Sub-stage" },
  { key: "progress", label: "Progress" },
  { key: "compliance", label: "Compliance" },
  { key: "responsible", label: "Responsible", needs: "projects.responsible" },
  { key: "vendor", label: "Agency" },
  { key: "est_value_cr", label: "Expected ₹ Cr" },
  { key: "vetted_cr", label: "Actual ₹ Cr, vetted" },
  { key: "last_updated_on", label: "Last updated" },
  { key: "start_on", label: "Start date", off: true },
  { key: "target_completion_on", label: "Expected completion", off: true },
  { key: "focus_area", label: "Application area", off: true },
  { key: "technology_area", label: "Technology area", off: true },
  { key: "investment_nature", label: "Nature of investment", off: true },
  { key: "report", label: "Report", needs: "projects.export", sortable: false },
];

// When a sub-stage is expected to close — what a reviewer asks first about an ongoing use case.
const expectedOn = (p) =>
  (p.phase && p.stage_dates?.[p.phase]?.expected_on) || p.phase_expected_on || null;

const Plain = ({ value }) => (
  <span className={value ? "" : "text-[11px] text-muted-foreground"}>{value || "—"}</span>
);

const CELLS = {
  code: (p) => <span className="font-data text-xs">{p.code}</span>,
  title: (p) => (
    <div className="max-w-[380px]">
      <div className="font-semibold">{p.title}</div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span>{p.focus_area || p.dept_shop || "—"}</span>
        {p.is_target_value && (
          <span className="whitespace-nowrap border border-primary px-1 py-0.5 text-[10px] font-bold uppercase text-primary">
            Target value
          </span>
        )}
        {p.unverified && (
          <UnverifiedBadge mark={p.unverified} compact testId={`unverified-${p.code}`} />
        )}
      </div>
    </div>
  ),
  plant: (p) => p.plant,
  status: (p) => <StageTag stage={p.status_view || p.status} />,
  term: (p) =>
    p.term ? (
      <span
        className="border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
        style={{ borderColor: TERM_COLORS[p.term], color: TERM_COLORS[p.term] }}
      >
        {p.term}
      </span>
    ) : (
      <span className="text-[11px] text-muted-foreground">To be updated</span>
    ),
  phase: (p) => (
    <div className="text-xs">
      <div className="font-medium">{p.phase_view || "—"}</div>
      {p.status_view === "Ongoing" &&
        (expectedOn(p) ? (
          <div className="font-data mt-0.5 text-[11px] text-muted-foreground">
            expected {expectedOn(p)}
          </div>
        ) : (
          <div className="mt-0.5 text-[11px] text-[hsl(var(--watch))]">
            expected date to be updated
          </div>
        ))}
    </div>
  ),
  progress: (p, { onOpen }) =>
    p.register_progress ? (
      <StatusPill
        progress={p.register_progress}
        reasons={p.register_progress_reasons || []}
        nextStep={
          p.register_progress === "delayed"
            ? "The unit to reach the sub-stage, or move its date with a reason."
            : "No date on this usecase has gone past."
        }
        testId={`project-status-${p.code}`}
        onClick={() => onOpen(p.id)}
      />
    ) : (
      <span className="text-[11px] text-muted-foreground">not in play</span>
    ),
  compliance: (p) =>
    p.compliance ? (
      <div data-testid={`compliance-${p.code}`}>
        <div className="font-data text-sm font-bold">
          {p.compliance.latest_pct ?? p.compliance.cumulative_pct}%
          <span className="ml-1 text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
            latest
          </span>
        </div>
        <div className="font-data text-[11px] text-muted-foreground">
          {p.compliance.cumulative_pct ?? "—"}% cumulative
        </div>
        <div className="text-[11px] text-muted-foreground">
          {p.compliance.days_read > 0
            ? `${p.compliance.days_met}/${p.compliance.days_read} day(s) at 100% · till `
            : "till "}
          {fmtDay(p.compliance.data_till || p.compliance.latest_pct_date)}
        </div>
      </div>
    ) : (
      <span className="text-[11px] text-muted-foreground">no readings yet</span>
    ),
  responsible: (p) => (
    <div className="text-xs" data-testid={`responsible-${p.code}`}>
      <div className="font-medium">
        {p.owner_name || p.responsible || (
          <span className="text-muted-foreground">Not named yet</span>
        )}
      </div>
      {p.responsible && p.responsible !== p.owner_name && (
        <div className="text-[11px] text-muted-foreground">
          {p.responsible === p.unit_spoc ? `${p.plant} SPOC · ` : ""}
          {p.responsible}
          {p.responsible_designation ? `, ${p.responsible_designation}` : ""}
        </div>
      )}
    </div>
  ),
  vendor: (p) => (
    <div className="text-xs">
      {p.vendor || "In-house"}
      {p.provider_type === "Startup" && (
        <span className="ml-1 border border-primary px-1 text-[9px] font-bold uppercase text-primary">
          Startup
        </span>
      )}
    </div>
  ),
  est_value_cr: (p) => (
    <span className="font-data text-xs font-semibold">{fmtCr(p.est_value_cr)}</span>
  ),
  vetted_cr: (p) => (
    <div className="text-xs" data-testid={`vetted-${p.code}`}>
      {p.impact?.actual_cr ? (
        <>
          <div className="font-data font-semibold text-[hsl(var(--healthy))]">
            {fmtCr(p.impact.actual_cr)}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {p.impact.signed_by}
            {p.impact.signed_on ? ` · ${p.impact.signed_on}` : ""}
          </div>
        </>
      ) : (
        <span className="text-[11px] text-muted-foreground">not vetted yet</span>
      )}
    </div>
  ),
  last_updated_on: (p) => (
    <div className="text-xs">
      <div className="font-data">{fmtDay(p.last_updated_on)}</div>
      <div className="text-[11px] text-muted-foreground">
        {p.last_updated_by || p.entered_by || "—"}
        {p.last_updated_by || p.entered_by ? " updated" : ""}
      </div>
      {p.approved_by && p.approved_by !== (p.last_updated_by || p.entered_by) && (
        <div className="text-[11px] text-muted-foreground" data-testid={`approved-by-${p.code}`}>
          {p.approved_by} approved
        </div>
      )}
    </div>
  ),
  start_on: (p) => <span className="font-data text-xs"><Plain value={p.start_on} /></span>,
  target_completion_on: (p) => (
    <span className="font-data text-xs">
      <Plain value={p.target_completion_on} />
    </span>
  ),
  focus_area: (p) => <span className="text-xs"><Plain value={p.focus_area} /></span>,
  technology_area: (p) => <span className="text-xs"><Plain value={p.technology_area} /></span>,
  investment_nature: (p) => <span className="text-xs"><Plain value={p.investment_nature} /></span>,
  report: (p) => <ProjectReportButton project={p} />,
};

export const ProjectRow = ({ p, columns, onOpen }) => (
  <tr
    data-testid={`project-row-${p.code}`}
    className="cursor-pointer transition-colors duration-200 hover:bg-muted"
    onClick={() => onOpen(p.id)}
  >
    {columns.map((key) => (
      <td key={key} className="px-4 py-3">
        {(CELLS[key] || (() => null))(p, { onOpen })}
      </td>
    ))}
  </tr>
);
